/**
 * IUROP Product Upsells (Complementary Products)
 * Handles smart add-to-cart: checks variants via JS, shows popup if needed
 */

class IuropProductUpsells {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.iurop-product-upsells__container');

    // Check if we need to load recommendations
    if (section.hasAttribute('data-product-recommendations')) {
      this.loadRecommendations();
    } else {
      this.init();
    }
  }

  async loadRecommendations() {
    const url = this.section.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const div = document.createElement('div');
      div.innerHTML = html;

      const recommendations = div.querySelector('.iurop-product-upsells');
      if (recommendations && recommendations.innerHTML.trim().length > 0) {
        this.section.innerHTML = recommendations.innerHTML;
        this.init();
      } else if (this.section.style.display !== 'none') {
        // No recommendations, hide section
        this.section.style.display = 'none';
      }
    } catch (e) {
      console.error('IUROP Upsells: Error loading recommendations', e);
    }
  }

  init() {
    // Initialize smart add buttons (check variants via JS)
    this.smartButtons = this.section.querySelectorAll('[data-upsell-add-smart]');
    this.smartButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleSmartAdd(e, btn));
    });

    // Reinitialize variant popup if needed
    if (window.iuropInitVariantPopup) {
      window.iuropInitVariantPopup();
    }

    // Patch popup render to handle object options (Shopify returns {name, position, values} instead of strings)
    if (window.iuropVariantPopup && !window.iuropVariantPopup._optionsPatched) {
      const origOpen = window.iuropVariantPopup.open.bind(window.iuropVariantPopup);
      window.iuropVariantPopup.open = async function(productHandle) {
        await origOpen(productHandle);
        // After open, normalize options if they are objects and re-render
        if (this.productData && this.productData.options) {
          const needsFix = this.productData.options.some(opt => typeof opt === 'object');
          if (needsFix) {
            this.productData.options = this.productData.options.map(opt =>
              typeof opt === 'object' ? (opt.name || opt.title || 'Opción') : opt
            );
            this.render();
          }
        }
      };
      window.iuropVariantPopup._optionsPatched = true;
    }
  }

  // Check if product has real options (not just "Title" / "Default Title")
  hasRealOptions(productData) {
    if (!productData.options || productData.options.length === 0) return false;
    const firstOpt = productData.options[0];
    const firstName = (typeof firstOpt === 'object' && firstOpt.name) ? firstOpt.name : firstOpt;
    if (productData.options.length === 1 && firstName === 'Title') return false;
    return true;
  }

  async handleSmartAdd(e, btn) {
    e.preventDefault();
    const productHandle = btn.dataset.productHandle;
    const fallbackVariantId = btn.dataset.variantId;

    console.log('[IUROP Debug] handleSmartAdd called', { productHandle, fallbackVariantId });

    if (!productHandle) return;

    const buttonText = btn.querySelector('.button-text');
    const spinner = btn.querySelector('.loading-overlay__spinner');
    const originalText = buttonText ? buttonText.textContent : '';

    try {
      // Fetch product data to check variants
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();

      console.log('[IUROP Debug] Product data:', {
        title: productData.title,
        options: productData.options,
        variantsCount: productData.variants?.length,
        hasRealOptions: this.hasRealOptions(productData)
      });
      console.log('[IUROP Debug] Popup available:', !!window.iuropVariantPopup, 'Popup element:', !!document.getElementById('iurop-variant-popup'));

      if (this.hasRealOptions(productData)) {
        // Product has real options: open popup
        if (window.iuropVariantPopup) {
          console.log('[IUROP Debug] Opening popup for', productHandle);
          window.iuropVariantPopup.open(productHandle);
        } else {
          console.error('[IUROP Debug] window.iuropVariantPopup is null!');
        }
      } else {
        // Single variant / no real options: add directly
        btn.disabled = true;
        if (spinner) spinner.classList.remove('hidden');
        if (buttonText) buttonText.style.opacity = '0';

        const variantId = productData.variants[0]?.id || fallbackVariantId;
        const addResult = await this.addToCart(variantId);

        if (spinner) spinner.classList.add('hidden');
        if (buttonText) {
          buttonText.style.opacity = '1';
          buttonText.textContent = '¡Añadido!';
        }

        if (!addResult || addResult.dispatched !== true) {
          await this.dispatchCartUpdate();
        }

        setTimeout(() => {
          if (buttonText) buttonText.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      }
    } catch (error) {
      console.error('IUROP Upsells: Error handling add', error);
      // Fallback: try adding with the variant ID from Liquid
      if (fallbackVariantId) {
        btn.disabled = true;
        if (spinner) spinner.classList.remove('hidden');
        if (buttonText) buttonText.style.opacity = '0';

        try {
          const addResult = await this.addToCart(fallbackVariantId);
          if (spinner) spinner.classList.add('hidden');
          if (buttonText) {
            buttonText.style.opacity = '1';
            buttonText.textContent = '¡Añadido!';
          }
          if (!addResult || addResult.dispatched !== true) {
            await this.dispatchCartUpdate();
          }
          setTimeout(() => {
            if (buttonText) buttonText.textContent = originalText;
            btn.disabled = false;
          }, 1500);
        } catch (err) {
          console.error('IUROP Upsells: Fallback add failed', err);
          btn.disabled = false;
          if (spinner) spinner.classList.add('hidden');
          if (buttonText) buttonText.style.opacity = '1';
        }
      }
    }
  }

  async addToCart(variantId) {
    if (typeof window.iuropAddToCart === 'function') {
      const result = await window.iuropAddToCart({
        variantId: String(variantId),
        quantity: 1,
        sourceId: 'iurop-upsell',
        openSidecartAfter: false
      });

      if (!result || !result.ok) {
        throw (result && result.error) || new Error('Add to cart failed');
      }

      return { dispatched: true, cart: result.cart || null, data: result.data || null };
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: String(variantId),
        quantity: 1
      })
    });

    if (!response.ok) throw new Error('Add to cart failed');
    const data = await response.json();
    return { dispatched: false, data };
  }

  async dispatchCartUpdate() {
    // Fetch current cart to get proper item count for Horizon's event format
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'iurop-upsell',
          data: {
            source: 'iurop-upsell',
            itemCount: cart.item_count
          }
        }
      }));
    } catch (e) {
      // Fallback dispatch without cart data
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'iurop-upsell',
          data: { source: 'iurop-upsell', itemCount: 0 }
        }
      }));
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const upsellSections = document.querySelectorAll('.iurop-product-upsells');
  upsellSections.forEach(section => new IuropProductUpsells(section));
});

// Theme editor support
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('iurop-product-upsells-section')) {
      const section = event.target.querySelector('.iurop-product-upsells');
      if (section) new IuropProductUpsells(section);
    }
  });
}
